// A Slack workspace already answering through another Boo takes this Boo as a
// Slack app of its own. The page walks through that: link the workspace once
// (the token Slack asks for to make apps), name this Boo, set its icon.

export type GuideStepState = "done" | "current" | "waiting";

export interface GuideStep {
  id: "link" | "name" | "icon";
  title: string;
  detail: string;
  state: GuideStepState;
  // What the viewer can do on this step: open the token dialog, or fill in the name.
  action: "link" | "form" | null;
  // Part of `detail` shown as a link.
  link?: { text: string; href: string };
}

export function secondBooGuide({
  holderName,
  place,
  workspaceReady,
  workspaceTeam,
  canLink,
  canConnect,
}: {
  // The Boo holding the Boo app, if its name is known.
  holderName: string | null;
  place: string;
  workspaceReady: boolean;
  workspaceTeam: string | null;
  canLink: boolean;
  canConnect: boolean;
}) {
  const steps: GuideStep[] = [
    {
      id: "link",
      title: "Let Boo make Slack apps in your workspace",
      detail: workspaceReady
        ? `Done. Linked to ${workspaceTeam ?? "your Slack workspace"}.`
        : canLink && canConnect
          ? "Slack only lets Boo create an app with a token from your workspace. You copy it from Slack once, and every Boo after this one skips this step."
          : "Slack only lets Boo create an app with a token from your workspace. An owner or admin of your organization links it once.",
      state: workspaceReady ? "done" : "current",
      action: !workspaceReady && canLink && canConnect ? "link" : null,
    },
    {
      id: "name",
      title: "Name this Boo in Slack",
      detail:
        workspaceReady && !canConnect
          ? "An owner or admin of this Boo picks its name and adds it to Slack."
          : "Pick the name people will see and message, like Acme Assistant. Slack asks you to allow the app.",
      state: workspaceReady ? "current" : "waiting",
      action: workspaceReady && canConnect ? "form" : null,
    },
    {
      id: "icon",
      title: "Set its icon in Slack",
      detail: "Once it's added, open the app in Slack app settings → Basic Information → Display Information.",
      state: "waiting",
      action: null,
      link: { text: "Slack app settings", href: "https://api.slack.com/apps" },
    },
  ];
  return {
    title: `${place} already has a Boo`,
    why: `${holderName ?? "Another Boo"} answers there through the Boo app, and a Slack workspace has one Boo app. To add this Boo too, give it a Slack app of its own. ${holderName ?? "That Boo"} keeps its Slack connection.`,
    steps,
  };
}
